/**
 * 将站内发品表单组装为 publishOrEdit 请求体。
 * - 新建：edit_type=0，不带 spu_name
 * - 编辑请用 buildSheinEditPayload（增量字段 + spu_name/skc_name/sku_code）
 */

import { buildSizeAttributeList } from '@/shein-platform/utils/sheinSizeChart';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  getFieldRule,
  parseFillInStandardMap,
} from '@/shein-platform/utils/sheinPublishFillInStandard';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { coerceSiteCurrencyMap } from '@/shein-platform/utils/sheinSiteList';
import { buildPublishSiteRrpInfoList } from '@/shein-platform/utils/sheinRrpPublish';
import { isLikelyBrowserReachableThumb } from '@/shein-platform/modules/products/components/SheinProductThumb';
import {
  filterProductAttributesToCategory,
  resolveProductAttributeValue,
} from '@/shein-platform/utils/sheinProductAttributeTemplate';
import { findAttributeValueByLabel } from '@/shein-platform/utils/sheinAttributeTemplate';
import {
  isBlankSheinAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/shein-platform/utils/sheinAttributeTemplate';

function hasCjkText(text) {
  return /[\u4e00-\u9fff]/.test(String(text || ''));
}

function parsePositiveAttributeId(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * 主规格颜色：优先模板平台值；自定义走 custom_attribute_value，禁止传 attribute_value_id=0。
 */
function buildSkcSaleAttribute(skc, colorAttr) {
  const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
  const attributeId = parsePositiveAttributeId(resolved.colorAttributeId || colorAttr?.attributeId);
  if (attributeId == null) return undefined;
  if (!isBlankSheinAttributeValueId(resolved.colorValueId)) {
    return {
      attribute_id: attributeId,
      attribute_value_id: Number(resolved.colorValueId),
    };
  }
  const extra = String(resolved.colorExtraValue || resolved.colorLabel || '').trim();
  if (!extra) return undefined;
  return {
    attribute_id: attributeId,
    custom_attribute_value: extra,
    language: hasCjkText(extra) ? 'zh-cn' : 'en',
  };
}

function buildSkuSaleAttributeList(sku, sizeAttr) {
  const attributeId = parsePositiveAttributeId(sku.sizeAttributeId || sizeAttr?.attributeId);
  if (attributeId == null) return [];
  if (!isBlankSheinAttributeValueId(sku.sizeValueId)) {
    return [{
      attribute_id: attributeId,
      attribute_value_id: Number(sku.sizeValueId),
    }];
  }
  const extra = String(sku.sizeExtraValue || sku.sizeLabel || '').trim();
  const asCustom = Boolean(sku.sizeIsCustom || sku.sizeExtraValue) || extra;
  if (!asCustom || !extra) return [];
  return [{
    attribute_id: attributeId,
    custom_attribute_value: extra,
    language: hasCjkText(extra) ? 'zh-cn' : 'en',
  }];
}

function imageUrlForPublish(image) {
  if (!image) return '';
  const shein = String(image.sheinUrl || '').trim();
  if (shein) return shein;
  return String(
    image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

/**
 * 发品体里附带一份「自家可读」的原图地址（站内 /uploads 受管路径或 http/https）。
 * 后端派生方块图(5)/色块图(6) 要读原图字节；若只给 SHEIN 测试 CDN 地址，
 * 服务器拉不到图就会整单发品失败，因此这里把原图地址一并带过去。
 */
function imageSourceUrlForPublish(image) {
  if (!image) return '';
  const candidate = String(
    image.previewUrl
    || image.publicUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
  if (!candidate) return '';
  if (/^https?:\/\//i.test(candidate)) return candidate;
  if (candidate.startsWith('/uploads/')) return candidate;
  return '';
}

/** 发布记录/草稿箱列表缩略图：优先浏览器可访问地址（本地 uploads），测试 CDN 常加载失败 */
function imageUrlForListPreview(image) {
  if (!image) return '';
  const local = String(
    image.previewUrl
    || image.publicUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
  const shein = String(image.sheinUrl || '').trim();
  if (local && isLikelyBrowserReachableThumb(local)) return local;
  if (shein && isLikelyBrowserReachableThumb(shein)) return shein;
  return local || shein || '';
}

/**
 * 标题多语种列表：必须包含店铺默认语种（form.shopDefaultLanguage / titleLanguage）。
 * 若表单语种与默认语种不一致，同时带上默认语种条目，避免平台报「商品标题不能为空」。
 */
function buildMultiLanguageNameList(form = {}) {
  const name = String(form.title || '').trim();
  const titleLang = String(form.titleLanguage || 'en').trim() || 'en';
  const defaultLang = String(form.shopDefaultLanguage || titleLang).trim() || titleLang;
  const list = [];
  const push = (language) => {
    const lang = String(language || '').trim();
    if (!lang || !name) return;
    if (list.some((row) => row.language === lang)) return;
    list.push({ language: lang, name });
  };
  push(defaultLang);
  push(titleLang);
  if (!list.length && name) {
    list.push({ language: titleLang, name });
  }
  return list;
}

function buildMultiLanguageDescList(form = {}) {
  const name = String(form.description || '').trim();
  if (!name) return [];
  const titleLang = String(form.titleLanguage || 'en').trim() || 'en';
  const defaultLang = String(form.shopDefaultLanguage || titleLang).trim() || titleLang;
  const list = [];
  const push = (language) => {
    const lang = String(language || '').trim();
    if (!lang) return;
    if (list.some((row) => row.language === lang)) return;
    list.push({ language: lang, name });
  };
  push(defaultLang);
  push(titleLang);
  return list;
}

function buildImageInfoList(skc) {
  const list = [];
  let sort = 1;
  if (skc.mainImage && imageUrlForPublish(skc.mainImage)) {
    const mainUrl = imageUrlForPublish(skc.mainImage);
    const mainSource = imageSourceUrlForPublish(skc.mainImage);
    const row = { image_sort: sort++, image_type: 1, image_url: mainUrl };
    // 后端按此地址裁切方块图(5)/色块图(6)，避免必须下载 SHEIN 测试 CDN 图
    if (mainSource && mainSource !== mainUrl) {
      row._ipe_source_url = mainSource;
    }
    list.push(row);
  }
  const showroom = skc.showroomSlots || {};
  ['front', 'back', 'detail', 'fabric', 'sizeChart'].forEach((key) => {
    const url = imageUrlForPublish(showroom[key]);
    if (url) list.push({ image_sort: sort++, image_type: 2, image_url: url });
  });
  (skc.detailImages || []).forEach((img) => {
    const url = imageUrlForPublish(img);
    if (url) list.push({ image_sort: sort++, image_type: 2, image_url: url });
  });
  if (skc.squareImage && imageUrlForPublish(skc.squareImage)) {
    // 方块图 type=5：历史草稿可能仍带字段；缺省时由后端按主图派生
    list.push({ image_sort: sort++, image_type: 5, image_url: imageUrlForPublish(skc.squareImage) });
  }
  // 色块图 type=6：提交时由后端按主图自动派生；表单字段仅兼容历史草稿
  const colorBlockUrl = imageUrlForPublish(skc.colorBlockImage);
  if (colorBlockUrl) {
    list.push({ image_sort: sort++, image_type: 6, image_url: colorBlockUrl });
  }
  return list;
}

/** 由件数值推导 quantity_type：1 单件 / 2 同品多件 */
function deriveQuantityType(quantity) {
  const n = Number(quantity);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n === 1 ? 1 : 2;
}

function buildSkuQuantityInfo(sku) {
  const rawQty = String(sku?.quantity ?? '').trim();
  if (!rawQty) return null;
  const quantity = Number(rawQty);
  if (!Number.isInteger(quantity) || quantity <= 0) return null;
  const unit = Number(sku?.quantityUnit || 1);
  const quantityUnit = [1, 2, 3].includes(unit) ? unit : 1;
  const quantityType = deriveQuantityType(quantity);
  if (quantityType == null) return null;
  return {
    quantity_type: quantityType,
    quantity_unit: quantityUnit,
    quantity,
  };
}

function normalizeSkuPkgCaps(skuPkgCaps) {
  const qty = skuPkgCaps?.quantityInfo;
  const pkg = skuPkgCaps?.packageType;
  return {
    quantityInfo: {
      show: qty?.show === true,
      required: qty?.required === true,
    },
    packageType: {
      show: pkg?.show === true,
      required: pkg?.required === true,
    },
  };
}

/** 仅当规范允许且实际写入了对应 SKU 字段时，才组装 fill_configuration_info */
function collectFillConfiguration(form = {}, caps = EMPTY_SKU_PACKAGE_CAPS) {
  const normalized = normalizeSkuPkgCaps(caps);
  let filledQuantity = false;
  let packageTypeToSku = false;
  (form.skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      if (normalized.quantityInfo.show && buildSkuQuantityInfo(sku)) {
        filledQuantity = true;
      }
      const pt = sku?.packageType;
      if (
        normalized.packageType.show
        && pt !== ''
        && pt != null
        && String(pt) !== '0'
      ) {
        packageTypeToSku = true;
      }
    });
  });
  if (!filledQuantity && !packageTypeToSku) return null;
  const info = {};
  if (filledQuantity) info.filled_quantity_to_sku = true;
  if (packageTypeToSku) info.fill_configuration_tags = ['PACKAGE_TYPE_TO_SKU'];
  return info;
}

/** stock_info_list：必填 inventory_num；有仓时带 supplier_warehouse_*（多仓时平台要求仓 ID） */
function buildStockInfoList(inventoryNum, form = {}) {
  const stock = {
    inventory_num: Number(inventoryNum ?? 0),
  };
  const warehouseId = String(form.warehouseId || '').trim();
  if (warehouseId) {
    stock.supplier_warehouse_id = warehouseId;
    const warehouseName = String(form.warehouseName || '').trim();
    if (warehouseName) {
      stock.supplier_warehouse_name = warehouseName;
    }
  }
  return [stock];
}

function collectProductAttrDefs(attrTpl) {
  const map = new Map();
  if (!attrTpl || typeof attrTpl !== 'object') return map;
  [
    attrTpl.productAttrs,
    attrTpl.requiredProduct,
    attrTpl.mainAttrs,
    attrTpl.otherAttrs,
  ].forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((attr) => {
      const id = String(attr?.attributeId ?? '').trim();
      if (!id || map.has(id)) return;
      map.set(id, attr);
    });
  });
  return map;
}

function valueMatchesAttrOption(attr, text) {
  if (!attr || !Array.isArray(attr.values) || !text) return false;
  return attr.values.some((row) => String(row?.id ?? '') === text);
}

/** 手输属性（克重/gsm 等）：平台要 extra_value，不能把数字当成 value_id */
function shouldSendAsHandInput(attr, text) {
  if (!text) return false;
  if (attr && Number(attr.mode) === 0) return true;
  if (attr && (!Array.isArray(attr.values) || attr.values.length === 0) && /^\d+(\.\d+)?$/.test(text)) {
    return true;
  }
  if (attr && /^\d+(\.\d+)?$/.test(text) && !valueMatchesAttrOption(attr, text)) {
    const name = `${attr.name || ''} ${attr.nameEn || ''}`;
    if (/克重|重量|gsm|g\/m|weight/i.test(name)) return true;
  }
  return false;
}

function buildProductAttributeList(productAttributes = {}, attrTpl = null) {
  const defs = collectProductAttrDefs(attrTpl);
  const scoped = filterProductAttributesToCategory(productAttributes, attrTpl);
  const list = [];
  Object.entries(scoped).forEach(([attributeId, value]) => {
    if (value == null || value === '') return;
    const attr = defs.get(String(attributeId));
    const resolvedValue = attr ? resolveProductAttributeValue(attr, value) : value;

    // 成分比例：[{ valueId, percent }]
    if (Array.isArray(resolvedValue) && resolvedValue.some((row) => row && typeof row === 'object' && ('percent' in row || 'valueId' in row))) {
      resolvedValue.forEach((row) => {
        if (!row?.valueId) return;
        const valueIdText = String(row.valueId).trim();
        if (shouldSendAsHandInput(attr, valueIdText)) {
          list.push({
            attribute_id: Number(attributeId),
            attribute_value_id: 0,
            attribute_extra_value: valueIdText,
          });
          return;
        }
        list.push({
          attribute_id: Number(attributeId),
          attribute_value_id: Number(valueIdText),
          attribute_extra_value: String(row.percent ?? ''),
        });
      });
      return;
    }

    // 多选：string[]
    if (Array.isArray(resolvedValue)) {
      resolvedValue.filter(Boolean).forEach((entry) => {
        const text = String(entry).trim();
        if (/^\d+$/.test(text)) {
          list.push({
            attribute_id: Number(attributeId),
            attribute_value_id: Number(text),
          });
          return;
        }
        const matched = findAttributeValueByLabel(attr, text);
        if (matched?.id && !isBlankSheinAttributeValueId(matched.id)) {
          list.push({
            attribute_id: Number(attributeId),
            attribute_value_id: Number(matched.id),
          });
          return;
        }
        list.push({
          attribute_id: Number(attributeId),
          attribute_value_id: 0,
          attribute_extra_value: text,
        });
      });
      return;
    }

    const text = String(resolvedValue).trim();
    if (shouldSendAsHandInput(attr, text)) {
      list.push({
        attribute_id: Number(attributeId),
        attribute_value_id: 0,
        attribute_extra_value: text,
      });
      return;
    }

    if (/^\d+$/.test(text)) {
      list.push({
        attribute_id: Number(attributeId),
        attribute_value_id: Number(text),
      });
      return;
    }

    list.push({
      attribute_id: Number(attributeId),
      attribute_value_id: 0,
      attribute_extra_value: text,
    });
  });
  return list;
}


function normalizeCurrencyCode(value) {
  // 发布规范返回的是「币种缩写」，保持原样（勿强行改写）
  return String(value || '').trim();
}

function normalizeSaleCurrencyCode(value) {
  return String(value || '').trim().toUpperCase();
}

/** 半托管供货价：两位小数字符串，与平台巴西站示例一致 */
function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return '';
  return num.toFixed(2);
}

/** 发品体里抽一个样例币种：供货价 cost_info 或自运营售价 price_info_list */
export function samplePublishPayloadCurrency(payload) {
  const sku = payload?.skc_list?.[0]?.sku_list?.[0]
    || payload?.skc_list?.find((row) => row?.sku_list?.[0])?.sku_list?.[0];
  return String(
    sku?.cost_info?.currency
    || sku?.price_info_list?.[0]?.currency
    || '',
  ).trim();
}

export function buildSheinPublishPayload(form, {
  isEdit = false,
  sizeAttrs = [],
  skuPkgCaps = EMPTY_SKU_PACKAGE_CAPS,
  shopType = '',
  modeCaps = null,
  siteList = null,
  siteCurrencyMap = null,
  attrTpl = null,
  standardRaw = null,
} = {}) {
  const caps = normalizeSkuPkgCaps(skuPkgCaps);
  const shopCaps = modeCaps || sheinShopCapabilities(shopType || form?.shopType);
  // 商品发布规范：非服装类目常额外要求上架要求 / 最小备货数量 / 建议零售价，
  // 这些字段不传会被平台整单驳回（报错只有字段名，用户对不上表单），
  // 因此凡是规范声明 required 的一律按规范补默认值/取值。
  const standardMap = standardRaw ? parseFillInStandardMap(standardRaw) : null;
  const ruleOf = (fieldKey) => (standardMap
    ? getFieldRule(standardMap, fieldKey)
    : { show: false, required: false, fieldKey: String(fieldKey || '') });
  // 规范里没出现该 key 时不能当成 show=false（缺省 safe default 只用于「有规范且明确声明」的场景）
  const ruleDeclared = (fieldKey) => Boolean(standardMap && standardMap.has(fieldKey));
  const shelfRequireRule = ruleOf('shelf_require');
  const shelfWayRule = ruleOf('shelf_way');
  const minimumStockRule = ruleOf('minimum_stock_quantity');
  const suggestPriceRule = ruleOf('suggest_price');
  const productAttributeList = buildProductAttributeList(form.productAttributes, attrTpl);
  const siteAbbrs = Array.isArray(form.selectedSites)
    ? [...new Set(form.selectedSites.map(String).map((s) => s.trim()).filter(Boolean))]
    : [];
  const mainSite = form.mainSite || 'shein';
  const saleCurrencyMap = coerceSiteCurrencyMap({
    siteCurrencyMap: siteCurrencyMap || form?.siteCurrencyMap,
    siteList: siteList || form?.siteList,
  });
  // cost_info.currency 必须来自「商品发布规范」写入的 form.currency，禁止用站点列表币种冒充
  const resolveCostCurrency = (skuCurrency) => (
    normalizeCurrencyCode(skuCurrency)
    || normalizeCurrencyCode(form.currency)
  );
  const defaultCurrency = resolveCostCurrency(null);
  const resolveSaleCurrencyForSite = (site, skuCurrency) => (
    saleCurrencyMap[String(site)]
    || normalizeSaleCurrencyCode(skuCurrency)
    || normalizeSaleCurrencyCode(form.saleCurrency)
    || normalizeSaleCurrencyCode(form.currency)
  );

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;

  const skcList = (form.skcList || [])
    .filter((skc) => (
      !isBlankSheinAttributeValueId(skc?.colorValueId)
      || String(skc?.colorExtraValue || skc?.colorLabel || '').trim()
    ))
    .map((skc, skcIndex) => {
    const resolvedSkc = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const skcSupplier = resolvedSkc.supplierCode || `${form.supplierCode || 'SKU'}-${skcIndex + 1}`;
    const saleAttribute = buildSkcSaleAttribute(resolvedSkc, colorAttr);

    const skuList = (resolvedSkc.skuRows || []).map((sku, skuIndex) => {
      const saleAttributeList = buildSkuSaleAttributeList(sku, sizeAttr);

      const row = {
        mall_state: Number(sku.mallState || 1),
        supplier_sku: sku.supplierSku || `${skcSupplier}-${skuIndex + 1}`,
        weight: Number(sku.weight || form.weightGrams || 0),
        weight_unit: form.weightUnit || 'g',
        length: String(sku.length || form.pkgLength || '10'),
        width: String(sku.width || form.pkgWidth || '10'),
        height: String(sku.height || form.pkgHeight || '10'),
        length_width_height_unit: form.dimUnit || 'cm',
        sale_attribute_list: saleAttributeList,
        stock_info_list: buildStockInfoList(sku.stock ?? form.stockQty ?? 0, form),
      };

      if (shopCaps.usesStopPurchase) {
        const stop = sku.stopPurchase != null ? Number(sku.stopPurchase) : Number(form.stopPurchase ?? 1);
        row.stop_purchase = Number.isFinite(stop) ? stop : 1;
      }

      if (caps.quantityInfo.show) {
        const quantityInfo = buildSkuQuantityInfo(sku);
        if (quantityInfo) {
          row.quantity_info = quantityInfo;
        }
      }

      if (caps.packageType.show) {
        const packageTypeRaw = sku.packageType;
        if (packageTypeRaw !== '' && packageTypeRaw != null && String(packageTypeRaw) !== '0') {
          // 文档类型为 string，取值 1–4（0 表示清空，表单不传）
          row.package_type = String(Number(packageTypeRaw));
        }
      }

      // minimum_stock_quantity：规范 required=true 必填（非服装类目常见），show=false 不可传。
      // 表单没提供入口时按最小值 1 兜底，避免整单被「最小备货数量未填写」驳回。
      if (minimumStockRule.show && minimumStockRule.required) {
        const rawMin = String(sku.minimumStockQuantity ?? form.minimumStockQuantity ?? '').trim();
        const minStock = /^\d+$/.test(rawMin) ? Number(rawMin) : 1;
        if (Number.isFinite(minStock) && minStock >= 1) {
          row.minimum_stock_quantity = String(minStock);
        }
      }

      const rawPrice = (sku.price != null && sku.price !== '')
        ? sku.price
        : form.supplyPrice;
      const money = (rawPrice != null && rawPrice !== '') ? formatMoney(rawPrice) : '';

      // 半托管/全托管：供货价 cost_info；自运营：售价 price_info_list
      if (shopCaps.usesCostInfo) {
        const costCurrency = resolveCostCurrency(sku.currency);
        if (money && Number(money) > 0 && costCurrency) {
          row.cost_info = {
            cost_price: money,
            currency: costCurrency,
          };
        }
      } else if (shopCaps.usesPriceInfo) {
        // SHEIN 文档：price_info_list 用 base_price + sub_site（勿传 site/price，平台会判「售价信息不能为空」）
        const siteListForPrice = siteAbbrs.length ? siteAbbrs : (form.priceSiteList || []);
        const priceRows = siteListForPrice
          .map((site) => {
            const currency = resolveSaleCurrencyForSite(site, sku.currency);
            if (!currency) return null;
            return {
              sub_site: String(site),
              base_price: Number(money),
              currency,
            };
          })
          .filter(Boolean);
        if (money && Number(money) > 0 && priceRows.length) {
          row.price_info_list = priceRows;
        }
      }

      if (sku.skuImage && imageUrlForPublish(sku.skuImage)) {
        row.image_info = {
          image_info_list: [{
            image_sort: 1,
            image_type: 1,
            image_url: imageUrlForPublish(sku.skuImage),
          }],
        };
      }

      // 建议零售价（SKU 维度，发品阶段随 publishOrEdit 提交）
      const siteRrp = buildPublishSiteRrpInfoList(sku);
      if (siteRrp.length) {
        row.site_rrp_info_list = siteRrp;
      }

      if (isEdit && sku.skuCode) row.sku_code = sku.skuCode;
      return row;
    });

    const fallbackCost = (form.supplyPrice != null && form.supplyPrice !== '')
      ? formatMoney(form.supplyPrice)
      : '';
    const fallbackSku = {
      mall_state: 1,
      supplier_sku: `${skcSupplier}-1`,
      weight: Number(form.weightGrams || 10),
      length: String(form.pkgLength || '10'),
      width: String(form.pkgWidth || '10'),
      height: String(form.pkgHeight || '10'),
      sale_attribute_list: [],
      stock_info_list: buildStockInfoList(form.stockQty || 0, form),
    };
    if (shopCaps.usesStopPurchase) {
      fallbackSku.stop_purchase = Number(form.stopPurchase ?? 1) || 1;
    }
    if (shopCaps.usesCostInfo && fallbackCost && Number(fallbackCost) > 0 && defaultCurrency) {
      fallbackSku.cost_info = {
        cost_price: fallbackCost,
        currency: defaultCurrency,
      };
    } else if (shopCaps.usesPriceInfo && fallbackCost && Number(fallbackCost) > 0) {
      const sites = siteAbbrs.length ? siteAbbrs : (form.priceSiteList || []);
      const priceRows = sites
        .map((site) => {
          const currency = resolveSaleCurrencyForSite(site, null);
          if (!currency) return null;
          return {
            sub_site: String(site),
            base_price: Number(fallbackCost),
            currency,
          };
        })
        .filter(Boolean);
      if (priceRows.length) {
        fallbackSku.price_info_list = priceRows;
      }
    }
    const skcPayload = {
      supplier_code: skcSupplier,
      image_info: { image_info_list: buildImageInfoList(skc) },
      sku_list: skuList.length ? skuList : [fallbackSku],
    };

    // shelf_way：全托管/半托管必填；自运营按发布规范（required 或表单有值才传）
    const wantShelfWay = shopCaps.mode === '全托管'
      || shopCaps.mode === '半托管'
      || (shelfWayRule.show && (shelfWayRule.required || form.shelfWay != null));
    if (wantShelfWay && !(ruleDeclared('shelf_way') && !shelfWayRule.show)) {
      skcPayload.shelf_way = String(form.shelfWay || '1');
    }
    // shelf_require：规范明确 show=false 时不可传（会触发 spmp0003）；
    // 规范 required=true 时必须传。以前只有全托管会带，
    // 非服装类目（家居/3C/美妆…）半托管/自运营因此报「上架要求未填写」整单驳回。
    const shelfRequireBlocked = ruleDeclared('shelf_require') && !shelfRequireRule.show;
    const wantShelfRequire = !shelfRequireBlocked && (
      shopCaps.usesShelfRequireAlways
      || form.shelfRequire != null
      || (shelfRequireRule.show && shelfRequireRule.required)
    );
    if (wantShelfRequire) {
      const req = form.shelfRequire != null
        ? Number(form.shelfRequire)
        : (shopCaps.usesShelfRequireAlways ? 1 : 0);
      if (Number.isFinite(req)) skcPayload.shelf_require = req;
    }

    if (saleAttribute) skcPayload.sale_attribute = saleAttribute;
    if (skc.skcName) skcPayload.skc_name = skc.skcName;
    if (String(skcPayload.shelf_way || '') === '2' && form.hopeOnSaleDate) {
      skcPayload.hope_on_sale_date = form.hopeOnSaleDate;
    }
    return skcPayload;
  });

  const payload = {
    category_id: Number(form.categoryId),
    product_type_id: form.productTypeId ? Number(form.productTypeId) : undefined,
    brand_code: form.brandCode || '',
    source_system: 'openapi',
    suit_flag: 0,
    supplier_code: form.supplierCode || '',
    multi_language_name_list: buildMultiLanguageNameList(form),
    multi_language_desc_list: buildMultiLanguageDescList(form),
    product_attribute_list: productAttributeList,
    skc_list: skcList,
  };

  const sizeList = buildSizeAttributeList(form, sizeAttrs);
  if (sizeList) payload.size_attribute_list = sizeList;

  if (!isEdit) {
    payload.edit_type = 0;
    // 自运营/半托管可用 site_list；全托管文档未列入，不传
    if (shopCaps.usesPublishSiteList !== false) {
      payload.site_list = [{
        main_site: mainSite || 'shein',
        sub_site_list: siteAbbrs,
      }];
    }
  } else if (form.spuName) {
    payload.spu_name = form.spuName;
  }

  const productVideoList = buildProductVideoList(form);
  if (productVideoList.length) {
    payload.product_video_list = productVideoList;
  }

  if (form.ipId) {
    payload.ip_character_list = [{
      ip_id: Number(form.ipId),
      ip_name: form.ipName || '',
      ip_name_cn: form.ipNameCn || '',
    }];
  }

  const fillConfiguration = collectFillConfiguration(form, caps);
  if (fillConfiguration) {
    payload.fill_configuration_info = fillConfiguration;
  }

  // suggested_retail_price（SPU 级对象，官方示例与 skc_list 平级）：
  // 规范 field_key=suggest_price 且 required=true 时平台必填，缺了整单驳回。
  // 取值优先级：表单填写 → SKU 建议零售价(RRP) → 刊登任务无 RRP 时用供货价兜底，
  // 否则批次刊登会因「建议零售价未填写」整单失败且用户无处可填。
  if (!isEdit) {
    const explicitRaw = String(form.suggestedRetailPrice ?? '').trim();
    let suggestPrice = Number(explicitRaw);
    let suggestCurrency = normalizeSaleCurrencyCode(form.suggestedRetailPriceCurrency);
    if (!(Number.isFinite(suggestPrice) && suggestPrice > 0)) {
      const rrpRow = (form.skcList || [])
        .flatMap((skc) => (Array.isArray(skc?.skuRows) ? skc.skuRows : []))
        .flatMap((sku) => (Array.isArray(sku?.siteRrpInfoList) ? sku.siteRrpInfoList : []))
        .find((row) => Number(String(row?.price ?? '').trim()) > 0);
      if (rrpRow) {
        suggestPrice = Number(rrpRow.price);
        suggestCurrency = suggestCurrency || normalizeSaleCurrencyCode(rrpRow.currency);
      }
    }
    if (!(Number.isFinite(suggestPrice) && suggestPrice > 0) && suggestPriceRule.required) {
      const fallbackPrice = String(form.supplyPrice ?? '').trim()
        || String(skcList[0]?.sku_list?.[0]?.cost_info?.cost_price ?? '').trim()
        || String(skcList[0]?.sku_list?.[0]?.price_info_list?.[0]?.base_price ?? '').trim();
      suggestPrice = Number(fallbackPrice);
    }
    const wantSuggestPrice = suggestPriceRule.show
      && (suggestPriceRule.required || explicitRaw);
    const currency = suggestCurrency || defaultCurrency;
    if (wantSuggestPrice && Number.isFinite(suggestPrice) && suggestPrice > 0 && currency) {
      payload.suggested_retail_price = {
        currency,
        price: Math.round(suggestPrice * 100) / 100,
      };
    }
  }

  // 站内发布记录列表预览（分类路径 / SKC 规格名）；后端写入摘要后剥掉再打 SHEIN
  payload._ipe_list_preview = buildIpeListPreview(form);
  // 编辑回填用完整表单（含印花图 / 本地预览 URL）；刊登任务无 draftId 时后端据此落草稿
  payload._ipe_edit_form = buildIpeEditFormSnapshot(form);

  return payload;
}

/**
 * 发品商品视频：最多取 sheinVideos / productVideos 首条；video_url 须为平台转换后地址。
 */
export function buildProductVideoList(form = {}) {
  const rawList = Array.isArray(form?.sheinVideos) && form.sheinVideos.length
    ? form.sheinVideos
    : (Array.isArray(form?.productVideos) ? form.productVideos : []);
  const first = rawList[0];
  if (!first) return [];
  const videoUrl = String(first.videoUrl || first.sheinUrl || '').trim();
  if (!videoUrl) {
    throw new Error('商品视频尚未转换完成，请重新上传');
  }
  const sites = Array.isArray(form?.selectedSites)
    ? [...new Set(form.selectedSites.map(String).map((s) => s.trim()).filter(Boolean))]
    : [];
  const entry = { video_url: videoUrl };
  if (sites.length) entry.sites = sites;
  return [entry];
}

function durablePrintImageUrl(image) {
  const candidates = [
    image?.ipeUrl,
    image?.publicUrl,
    image?.url,
    image?.previewUrl,
    image?.sheinUrl,
  ];
  let sheinFallback = '';
  for (const raw of candidates) {
    const url = String(raw || '').trim();
    if (!url) continue;
    const lower = url.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:')) continue;
    if (lower.includes('imgdeal-test') || lower.includes('-test01.') || lower.includes('sheincorp.cn')) {
      if (!sheinFallback) sheinFallback = url;
      continue;
    }
    const uploadsIdx = url.indexOf('/uploads/');
    if (uploadsIdx >= 0
      && (lower.includes('localhost') || lower.includes('127.0.0.1') || url.startsWith('/'))) {
      return url.slice(uploadsIdx).split('?')[0];
    }
    if (/^https?:\/\//i.test(url) || url.startsWith('/')) return url;
  }
  return sheinFallback;
}

function slimPrintImagesForPersist(images) {
  return (Array.isArray(images) ? images : [])
    .map((img) => {
      const url = durablePrintImageUrl(img);
      if (!url) return null;
      const name = String(img?.name || '').trim();
      return {
        id: img?.id || '',
        name,
        ipeUrl: url,
        publicUrl: url,
        url,
        previewUrl: url,
        sheinUrl: '',
      };
    })
    .filter(Boolean);
}

function slimSheinVideosForPersist(videos) {
  return (Array.isArray(videos) ? videos : [])
    .slice(0, 1)
    .map((v) => {
      const videoUrl = String(v?.videoUrl || '').trim();
      const sheinUrl = String(v?.sheinUrl || '').trim();
      const ipeUrl = String(v?.ipeUrl || '').trim();
      const taskId = String(v?.taskId || '').trim();
      const name = String(v?.name || '').trim();
      if (!videoUrl && !sheinUrl && !ipeUrl) return null;
      return {
        videoUrl,
        sheinUrl: sheinUrl || videoUrl,
        ipeUrl,
        taskId,
        name,
      };
    })
    .filter(Boolean);
}

/**
 * 发品成功后编辑页回填：只固化系统印花图地址（刷新后仍可按地址展示）。
 */
export function buildIpeEditFormSnapshot(form = {}) {
  const src = form && typeof form === 'object' ? form : {};
  const printImages = slimPrintImagesForPersist(src.printImages);
  const sheinVideos = slimSheinVideosForPersist(src.sheinVideos || src.productVideos);
  return {
    ...src,
    printImages,
    sheinVideos,
    ipePrintImageNames: Array.isArray(src.ipePrintImageNames) && src.ipePrintImageNames.length
      ? src.ipePrintImageNames
      : printImages.map((img) => img.name).filter(Boolean),
    skcList: (Array.isArray(src.skcList) ? src.skcList : []).map((skc) => ({
      ...skc,
      squareImage: null,
      colorBlockImage: null,
    })),
  };
}

/** 列表展示用：可读分类与规格名（发品体只有 attribute_value_id） */
export function buildIpeListPreview(form = {}) {
  const sizeNameById = { ...(form.sizeValueLabels || {}) };
  return {
    // 复色 partialEdit 不传 multi_language_name_list，发布记录标题从这里回填
    title: String(form.title || '').trim(),
    categoryLabel: String(form.categoryPathLabel || form.categoryName || '').trim(),
    skcItems: (form.skcList || []).map((skc) => ({
      // 优先用户填写的自定义色名，再回退模板展示名（勿用平台英文字段覆盖中文复色名）
      colorLabel: String(skc?.colorExtraValue || skc?.colorLabel || '').trim(),
      skcName: String(skc?.skcName || '').trim(),
      // 列表缩略图：优先本地/公网可访问地址（勿用测试 CDN，否则发布记录首屏空白）
      imageUrl: imageUrlForListPreview(skc?.mainImage)
        || imageUrlForListPreview(skc?.squareImage)
        || imageUrlForListPreview(skc?.colorBlockImage)
        || imageUrlForListPreview(Array.isArray(skc?.detailImages) ? skc.detailImages[0] : null)
        || '',
      merchantSkus: (skc?.skuRows || []).map((sku) => {
        const sizeId = sku?.sizeValueId != null ? String(sku.sizeValueId) : '';
        return {
          sizeLabel: String(
            sku?.sizeLabel
            || sizeNameById[sizeId]
            || '',
          ).trim(),
          supplierSku: String(sku?.supplierSku || '').trim(),
        };
      }),
    })),
  };
}
